import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './SourceBuilderTabView.scss';
import {
  ExCard,
  ExIcon,
  ExButton,
  ButtonFlavor,
  ButtonType,
  ButtonSize,
  ExBadge,
  BadgeColor,
  BadgeShape,
  BadgeSize,
  ExLink,
  TargetVariant,
  LinkSize,
  ExAlertBanner,
  AlertBannerType,
  AlertBannerVariant,
  ExTooltip,
  TooltipPosition,
  TooltipAlignment,
} from '@boomi/exosphere';
import { useBoomiAgentFoundationContext } from '@context/BoomiAgentFoundationContext';
import { hasPrivilege } from '@utils/data/rbacService';
import UserPrivilege from '@utils/data/userPrivileges';

function SourcesBuilderTabView({ hasAgentCreatePermission, userPrivileges }: any) {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const {
    setShowBuilderDialog,
  } = useBoomiAgentFoundationContext();

  const hasControlPlaneAdminAccess = hasPrivilege(
    userPrivileges,
    UserPrivilege.CONTROL_PLANE_ADMIN,
  );
  const hasControlPlaneUserAccess = hasPrivilege(
    userPrivileges,
    UserPrivilege.CONTROL_PLANE_USER,
  );
  const hasControlPlaneAccess = hasControlPlaneAdminAccess || hasControlPlaneUserAccess;

  const handleAddMcpSource = () => {
    setShowBuilderDialog(false);
    document.body.style.overflow = 'unset';
    navigate('/add-source');
  };

  const handleExploreMcpServers = () => {
    setShowBuilderDialog(false);
    document.body.style.overflow = 'unset';
    navigate('/explore-mcp-servers');
  };

  const handleAddAcpSource = () => {
    setShowBuilderDialog(false);
    document.body.style.overflow = 'unset';
    navigate('/add-acp-source');
  };

  return (
    <div className="source-builder-container">
      <div className="agent-tool-info">
        {t('sources_builder_text')}
        <ExLink
          href="https://help.boomi.com/docs/Atomsphere/Platform/ai_Creating_and_managing_sources"
          target={TargetVariant.BLANK}
          size={LinkSize.SMALL}
          className="tooltip-link designer-dialog-link"
          trailingIcon="Open in new window"
        >
          {t('learn_more')}
        </ExLink>
      </div>

      {!hasControlPlaneAccess && (
        <div style={{ marginBottom: '24px' }}>
          <ExAlertBanner
            type={AlertBannerType.WARNING}
            variant={AlertBannerVariant.SUBTLE}
            open
            style={{ margin: 0 }}
          >
            <span style={{ whiteSpace: 'normal' }}>
              {t('api_control_plane_access_message')}
              {' '}
              <a
                href="https://help.boomi.com/docs/Atomsphere/API%20Management/Topics/cp-Gettting_started"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: 'var(--exo-color-font-link)',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  display: 'inline',
                  fontSize: 'var(--exo-font-size-small)',
                  fontFamily: 'var(--exo-font-family)',
                  fontWeight: 'var(--exo-font-weight-regular)',
                  lineHeight: 'inherit',
                }}
              >
                {t('api_control_plane_learn_more')}
              </a>
            </span>
          </ExAlertBanner>
        </div>
      )}

      <div className="template-cards-container">
        {hasAgentCreatePermission && (
          <>
            <ExCard
              className="template-card source-card"
              data-testid="designer-dialog-agent-no-template-card"
            >
              <div
                slot="header"
                className="template-header"
                data-testid="source-header"
                style={{ position: 'relative' }}
              >
                <div className="source-image-container">
                  <ExIcon className="mcp-icon" slot="icon" icon="MCP" />
                </div>
                <ExBadge
                  color={BadgeColor.NAVY}
                  shape={BadgeShape.SQUARED}
                  size={BadgeSize.SMALL}
                  style={{ position: 'absolute', top: '8px', right: '8px' }}
                >
                  {t('tools')}
                </ExBadge>
              </div>
              <div className="template-content">
                <div className="agent-name">{t('model_context_protocol')}</div>
                <div className="agent-description">
                  {t('model_context_protocol_description')}
                </div>
              </div>
              <div className="card-overlay">
                <ExButton
                  flavor={ButtonFlavor.BRANDED}
                  type={ButtonType.PRIMARY}
                  size={ButtonSize.DEFAULT}
                  data-testid="agent-designer-source-configure-mcp-button"
                  onClick={handleAddMcpSource}
                >
                  {t('mcp_configure_manually')}
                </ExButton>
                {!hasControlPlaneAccess ? (
                  <ExButton
                    slot="anchor"
                    flavor={ButtonFlavor.BRANDED}
                    type={ButtonType.SECONDARY}
                    size={ButtonSize.DEFAULT}
                    data-testid="agent-designer-source-explore-mcp-button"
                    onClick={handleExploreMcpServers}
                    disabled
                  >
                    {t('mcp_explore_servers')}
                  </ExButton>
                ) : (
                  <ExButton
                    flavor={ButtonFlavor.BRANDED}
                    type={ButtonType.SECONDARY}
                    size={ButtonSize.DEFAULT}
                    data-testid="agent-designer-source-explore-mcp-button"
                    onClick={handleExploreMcpServers}
                  >
                    {t('mcp_explore_servers')}
                  </ExButton>
                )}
              </div>
            </ExCard>

            {/* ACP CARD */}
            <ExCard
              className="template-card source-card"
              data-testid="designer-dialog-agent-no-template-card"
            >
              <div
                slot="header"
                className="template-header"
                data-testid="source-header"
                style={{ position: 'relative' }}
              >
                <div className="source-image-container">
                  <ExIcon className="acp-icon" slot="icon" icon="ACP" />
                </div>
                <ExBadge
                  color={BadgeColor.NAVY}
                  shape={BadgeShape.SQUARED}
                  size={BadgeSize.SMALL}
                  style={{ position: 'absolute', top: 8, right: 8 }}
                >
                  {t('tools')}
                </ExBadge>
              </div>

              <div className="template-content" style={{ minHeight: '100px' }}>
                <div className="agent-name">{t('agent_context_protocol')}</div>
                <div className="agent-description">
                  {t('agent_context_protocol_description')}
                </div>
                <div className="card-overlay">
                  {!hasControlPlaneAccess ? (
                    <ExTooltip
                      position={TooltipPosition.TOP}
                      alignment={TooltipAlignment.MIDDLE}
                    >
                      <div>{t('api_control_plane_access_message_acp_card')}</div>
                      <ExButton
                        slot="anchor"
                        flavor={ButtonFlavor.BRANDED}
                        type={ButtonType.PRIMARY}
                        size={ButtonSize.DEFAULT}
                        className="button-margin"
                        data-testid="agent-designer-source-create-acp-button"
                        disabled
                      >
                        {t('agent_context_protocol_add_source')}
                      </ExButton>
                    </ExTooltip>
                  ) : (
                    <ExButton
                      flavor={ButtonFlavor.BRANDED}
                      type={ButtonType.PRIMARY}
                      size={ButtonSize.DEFAULT}
                      className="button-margin"
                      data-testid="agent-designer-source-create-acp-button"
                      onClick={handleAddAcpSource}
                    >
                      {t('agent_context_protocol_add_source')}
                    </ExButton>
                  )}
                </div>
              </div>
            </ExCard>
          </>
        )}
      </div>
    </div>
  );
}
export default SourcesBuilderTabView;
